import React, { useMemo, useState } from 'react'
import {
	EuiButton,
	EuiButtonIcon,
	EuiFieldNumber,
	EuiFieldText,
	EuiFlexGroup,
	EuiFlexItem,
	EuiFormRow,
	EuiPopover,
	EuiSelectable,
	EuiSelectableOption,
	EuiButtonEmpty,
} from '@equipmentshare/ds2'
import { AddCircleLineIcon, DeleteBin7LineIcon, FileTextLineIcon } from '@equipmentshare/ds2-icons'
import { Part, ReservationTargetType } from '../../../graphql'
import PartPicker from '../../../components/PartPicker'
import WorkOrderPicker from '../../../components/WorkOrderPicker'
import InvoicePicker from '../../../components/InvoicePicker'
import OrderPicker from '../../../components/OrderPicker'
import NotesModal from '../../requests/components/NotesModal'
import { TransferOrderLineItem } from '../hooks/useTransferOrderForm'

const COLUMN_WIDTHS = {
	quantity: 90,
	itemIsFor: 220,
	noteButton: 120,
	deleteButton: 40,
} as const

type TargetTypeOption = {
	value: ReservationTargetType | '',
	label: string,
}

const TARGET_TYPE_OPTIONS: TargetTypeOption[] = [
	{ value: '', label: 'Stock' },
	{ value: ReservationTargetType.WorkOrder, label: 'Work Order' },
	{ value: ReservationTargetType.Invoice, label: 'Invoice' },
	{ value: ReservationTargetType.Order, label: 'Order' },
]

export type TransferOrderLineItemFormProps = {
	lineItem: TransferOrderLineItem,
	index: number,
	onUpdate: ( index: number, updates: Partial<TransferOrderLineItem> ) => void,
	onRemove: ( index: number ) => void,
	isOnlyItem: boolean,
	showValidationErrors?: boolean,
	branchId?: string,
}

export default function TransferOrderLineItemForm( {
	lineItem,
	index,
	onUpdate,
	onRemove,
	isOnlyItem,
	showValidationErrors = false,
	branchId,
}: TransferOrderLineItemFormProps ) {
	const [ isTargetTypePopoverOpen, setIsTargetTypePopoverOpen ] = useState( false )
	const [ isNotesModalOpen, setIsNotesModalOpen ] = useState( false )

	const currentTargetTypeLabel = useMemo( () => {
		switch ( lineItem.allocationTargetType ) {
			case ReservationTargetType.WorkOrder: return 'WO'
			case ReservationTargetType.Invoice: return 'INV'
			case ReservationTargetType.Order: return 'ORD'
			default: return 'Stock'
		}
	}, [ lineItem.allocationTargetType ] )

	const selectableOptions = useMemo( (): EuiSelectableOption[] => {
		return TARGET_TYPE_OPTIONS.map( ( option ) => ( {
			label: option.label,
			data: option,
			checked: option.value === lineItem.allocationTargetType ? 'on' : undefined,
		} ) )
	}, [ lineItem.allocationTargetType ] )

	const handlePartChange = ( partId: number, selectedPart?: Part ) => {
		onUpdate( index, {
			productId: partId ? partId.toString() : '',
			selectedPart: selectedPart ?? null,
		} )
	}

	const handleQuantityChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		const value = parseInt( event.target.value, 10 )
		onUpdate( index, { quantityRequested: isNaN( value ) ? 0 : value } )
	}

	const handleTargetTypeSelect = ( options: EuiSelectableOption[] ) => {
		const selectedOption = options.find( ( opt ) => opt.checked === 'on' )
		setIsTargetTypePopoverOpen( false )
		if ( selectedOption?.data ) {
			const targetOption = selectedOption.data as TargetTypeOption
			onUpdate( index, {
				allocationTargetType: targetOption.value,
				allocationTargetId: '',
			} )
		}
	}

	const prependElement = useMemo( () => (
		<EuiPopover
			anchorPosition="downCenter"
			panelPaddingSize="none"
			isOpen={isTargetTypePopoverOpen}
			closePopover={() => setIsTargetTypePopoverOpen( false )}
			button={
				<EuiButtonEmpty
					onClick={() => setIsTargetTypePopoverOpen( true )}
					iconType="arrowDown"
					iconSide="right"
					color="text"
				>
					{currentTargetTypeLabel}
				</EuiButtonEmpty>
			}
		>
			<EuiSelectable
				onBlur={() => setIsTargetTypePopoverOpen( false )}
				style={{ width: '170px' }}
				listProps={{ style: { textAlign: 'center' }, showIcons: true }}
				singleSelection
				onChange={handleTargetTypeSelect}
				options={selectableOptions}
			>
				{( list ) => list}
			</EuiSelectable>
		</EuiPopover>
	), [ isTargetTypePopoverOpen, currentTargetTypeLabel, selectableOptions, handleTargetTypeSelect ] )

	const hasNotes = lineItem.lineNote && lineItem.lineNote.trim().length > 0

	return (
		<>
			<EuiFlexGroup direction="row" gutterSize="s" alignItems="center" wrap={false}>
				{/* Part picker */}
				<EuiFlexItem>
					<PartPicker
						id={`part-picker-${index}`}
						label=""
						onChange={handlePartChange}
						selectedPart={lineItem.selectedPart}
						formRowProps={{
							display: 'center',
							isInvalid: showValidationErrors && !lineItem.productId,
							error: showValidationErrors && !lineItem.productId ? 'Product is required' : undefined,
						}}
						fullWidth
						placeholder="Select a product"
						data-testid={`part-picker-${index}`}
					/>
				</EuiFlexItem>

				{/* Quantity */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.quantity }}>
					<EuiFormRow display="center">
						<EuiFieldNumber
							value={lineItem.quantityRequested || ''}
							onChange={handleQuantityChange}
							min={0}
							step={1}
							isInvalid={showValidationErrors && ( !lineItem.quantityRequested || lineItem.quantityRequested < 1 )}
							placeholder="0"
							data-testid={`quantity-field-${index}`}
						/>
					</EuiFormRow>
				</EuiFlexItem>

				{/* Allocation */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.itemIsFor }}>
					{lineItem.allocationTargetType === ReservationTargetType.WorkOrder ? (
						<WorkOrderPicker
							selectedWorkOrderId={lineItem.allocationTargetId}
							onChange={( id ) => onUpdate( index, { allocationTargetId: id } )}
							data-testid={`work-order-picker-${index}`}
							formRowProps={{
								display: 'center',
								label: '',
								labelType: 'legend',
								isInvalid: showValidationErrors && !lineItem.allocationTargetId,
								error: showValidationErrors && !lineItem.allocationTargetId ? 'Work order is required' : undefined,
							}}
							prepend={prependElement}
							showPrefix={false}
							branchId={branchId}
						/>
					) : lineItem.allocationTargetType === ReservationTargetType.Invoice ? (
						<InvoicePicker
							selectedInvoiceId={lineItem.allocationTargetId}
							onChange={( id ) => onUpdate( index, { allocationTargetId: id } )}
							data-testid={`invoice-picker-${index}`}
							formRowProps={{
								display: 'center',
								label: '',
								labelType: 'legend',
								isInvalid: showValidationErrors && !lineItem.allocationTargetId,
								error: showValidationErrors && !lineItem.allocationTargetId ? 'Invoice is required' : undefined,
							}}
							prepend={prependElement}
							branchId={branchId}
						/>
					) : lineItem.allocationTargetType === ReservationTargetType.Order ? (
						<OrderPicker
							selectedOrderId={lineItem.allocationTargetId}
							onChange={( id ) => onUpdate( index, { allocationTargetId: id } )}
							data-testid={`order-picker-${index}`}
							formRowProps={{
								display: 'center',
								label: '',
								labelType: 'legend',
								isInvalid: showValidationErrors && !lineItem.allocationTargetId,
								error: showValidationErrors && !lineItem.allocationTargetId ? 'Order is required' : undefined,
							}}
							prepend={prependElement}
							branchId={branchId}
						/>
					) : (
						<EuiFormRow display="center">
							<EuiFieldText disabled placeholder="" prepend={prependElement} />
						</EuiFormRow>
					)}
				</EuiFlexItem>

				{/* Notes button */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.noteButton }}>
					<EuiFormRow display="center">
						<EuiButton
							iconType={hasNotes ? FileTextLineIcon : AddCircleLineIcon}
							onClick={() => setIsNotesModalOpen( true )}
							color={hasNotes ? 'success' : 'primary'}
							fill={false}
							data-testid={`add-note-button-${index}`}
						>
							{hasNotes ? 'Edit Note' : 'Add Note'}
						</EuiButton>
					</EuiFormRow>
				</EuiFlexItem>

				{/* Delete button */}
				<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.deleteButton }}>
					<EuiFormRow display="center">
						<EuiButtonIcon
							iconType={DeleteBin7LineIcon}
							color="danger"
							onClick={() => !isOnlyItem && onRemove( index )}
							disabled={isOnlyItem}
							aria-label={`Remove line item ${index + 1}`}
							data-testid={`remove-line-item-${index}`}
							display="base"
							size="m"
						/>
					</EuiFormRow>
				</EuiFlexItem>
			</EuiFlexGroup>

			<NotesModal
				isOpen={isNotesModalOpen}
				onClose={() => setIsNotesModalOpen( false )}
				notes={lineItem.lineNote ?? ''}
				onSave={( notes ) => onUpdate( index, { lineNote: notes } )}
				title={`${hasNotes ? 'Edit' : 'Add'} Note - Line Item ${index + 1}`}
				placeholder="Add notes for this line item..."
			/>
		</>
	)
}
